import type { ProductCategory, RiderSale } from "@artisancode/api-types";

const MONTHS_ID = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

export function formatReportDate(isoDate: string): string {
  if (!isoDate) return "-";
  const parts = isoDate.split("-").map(Number);
  const year = parts[0] ?? 0;
  const month = (parts[1] ?? 1) - 1;
  const day = parts[2] ?? 1;
  return `${day} ${MONTHS_ID[month] ?? ""} ${year}`;
}

export function formatReportPeriod(dateFrom: string, dateTo?: string): string {
  if (!dateFrom && !dateTo) return "-";
  if (!dateTo || dateFrom === dateTo) {
    return formatReportDate(dateFrom);
  }
  return `${formatReportDate(dateFrom)} – ${formatReportDate(dateTo)}`;
}

export function formatReportRp(amount: number): string {
  const rounded = Math.round(amount || 0);
  if (rounded === 0) return "Rp0";
  if (rounded < 0) {
    return `-Rp${Math.abs(rounded).toLocaleString("id-ID")}`;
  }
  return `Rp${rounded.toLocaleString("id-ID")}`;
}

export interface RiderProcessedLine {
  productId: string;
  productName: string;
  category: ProductCategory;
  dispatchedQty: number;
  soldQty: number;
  remainingQty: number;
  sellingPrice: number;
  subtotal: number;
}

export interface RiderReportSummaryRow {
  riderId: string;
  riderName: string;
  status: "draft" | "done" | "paid";
  workDaysCount: number;
  soldQty: number;
  remainingQty: number;
  dispatchedQty: number;
  omzet: number;
  qris: number;
  cash: number;
  other: number;
  pembayaran: number;
  selisih: number;
  bonus: number;
  notes: string | null;
  lines: RiderProcessedLine[];
}

export interface ProductStockRekapItem {
  productId: string;
  productName: string;
  category: ProductCategory;
  diambil: number;
  terjual: number;
  sisa: number;
  omzet: number;
}

export interface ProductStockRekapCategory {
  category: ProductCategory;
  title: string;
  unit: string;
  items: ProductStockRekapItem[];
  totalDiambil: number;
  totalTerjual: number;
  totalSisa: number;
  totalOmzet: number;
}

export interface RiderSaleReportData {
  dateFrom: string;
  dateTo: string;
  isMultiDay: boolean;
  formattedPeriod: string;
  workspaceName: string;
  status: "Selesai" | "DRAFT / BERMASALAH";
  draftCount: number;
  discrepancyCount: number;
  issueSummary?: string;

  totalOmzet: number;
  totalMinumanCup: number;
  totalRotiPcs: number;
  sentenceOmzet: string;

  totalPembayaran: number;
  totalQris: number;
  totalCash: number;
  totalOther: number;
  totalSelisih: number;
  totalBonus: number;
  hasOtherPayment: boolean;
  sentencePembayaran: string;

  riders: RiderReportSummaryRow[];
  totals: {
    soldQty: number;
    remainingQty: number;
    dispatchedQty: number;
    omzet: number;
    qris: number;
    cash: number;
    other: number;
    pembayaran: number;
    selisih: number;
    bonus: number;
  };

  discrepancyNotes: {
    riderName: string;
    note: string;
  }[];

  rekapCategories: ProductStockRekapCategory[];
  riderPages: RiderReportSummaryRow[][];
}

export interface BuildReportOptions {
  sales: RiderSale[];
  dateFrom: string;
  dateTo?: string;
  workspaceName?: string;
  riderOrder?: string[];
  productOrder?: string[];
}

export function buildRiderSaleReport(
  options: BuildReportOptions,
): RiderSaleReportData {
  const {
    sales,
    dateFrom,
    dateTo: rawDateTo,
    workspaceName = "JAM Coffee & Bakery",
    riderOrder,
    productOrder,
  } = options;

  const dateTo = rawDateTo || dateFrom;
  const isMultiDay = dateFrom !== dateTo;
  const formattedPeriod = formatReportPeriod(dateFrom, dateTo);

  // 1. Filter out cancelled sales
  const activeSales = sales.filter((s) => s.status !== "cancelled");

  // 2. Group by rider
  const riderMap = new Map<
    string,
    {
      riderId: string;
      riderName: string;
      salesCount: number;
      totalBonus: number;
      statuses: ("draft" | "done" | "paid")[];
      linesMap: Map<string, RiderProcessedLine>;
      paymentMap: Map<string, number>;
      notesList: string[];
    }
  >();

  for (const sale of activeSales) {
    const key = sale.riderId || sale.riderName;
    let entry = riderMap.get(key);
    if (!entry) {
      entry = {
        riderId: sale.riderId,
        riderName: sale.riderName,
        salesCount: 0,
        totalBonus: 0,
        statuses: [],
        linesMap: new Map(),
        paymentMap: new Map(),
        notesList: [],
      };
      riderMap.set(key, entry);
    }

    entry.salesCount += 1;
    entry.totalBonus += sale.totalBonus ?? 0;

    if (sale.status !== "cancelled") {
      entry.statuses.push(sale.status);
    }

    if (sale.notes?.trim()) {
      entry.notesList.push(sale.notes.trim());
    }

    // Merge lines
    for (const line of sale.lines ?? []) {
      const lineKey = line.productId || line.productName;
      let existingLine = entry.linesMap.get(lineKey);
      if (!existingLine) {
        existingLine = {
          productId: line.productId,
          productName: line.productName,
          category: line.category,
          dispatchedQty: 0,
          soldQty: 0,
          remainingQty: 0,
          sellingPrice: line.sellingPrice,
          subtotal: 0,
        };
        entry.linesMap.set(lineKey, existingLine);
      }
      existingLine.dispatchedQty += line.dispatchedQty ?? 0;
      existingLine.soldQty += line.soldQty ?? 0;
      existingLine.remainingQty += line.remainingQty ?? 0;
      existingLine.subtotal += line.subtotal ?? 0;
      if (line.sellingPrice) {
        existingLine.sellingPrice = line.sellingPrice;
      }
    }

    // Merge payments
    for (const payment of sale.payments ?? []) {
      const current = entry.paymentMap.get(payment.method) ?? 0;
      entry.paymentMap.set(payment.method, current + (payment.amount ?? 0));
    }
  }

  // 3. Process each rider row
  const riders: RiderReportSummaryRow[] = [];
  let hasOtherPayment = false;

  for (const entry of riderMap.values()) {
    // Resolve combined status: draft if any draft, else done if any done, else paid
    let status: "draft" | "done" | "paid" = "paid";
    if (entry.statuses.includes("draft")) {
      status = "draft";
    } else if (entry.statuses.includes("done")) {
      status = "done";
    }

    // Payment breakdown
    let cash = 0;
    let qris = 0;
    let other = 0;

    for (const [method, amount] of entry.paymentMap.entries()) {
      if (method === "cash") {
        cash += amount;
      } else if (method === "qris" || method === "app_qris") {
        qris += amount;
      } else {
        other += amount;
      }
    }

    if (other > 0) {
      hasOtherPayment = true;
    }

    const pembayaran = cash + qris + other;

    // Convert lines map to array and sort
    const rawLines = Array.from(entry.linesMap.values());
    rawLines.sort((a, b) => {
      if (productOrder) {
        const idxA = productOrder.indexOf(a.productId);
        const idxB = productOrder.indexOf(b.productId);
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
      }
      return a.productName.localeCompare(b.productName);
    });

    const dispatchedQty = rawLines.reduce((s, l) => s + l.dispatchedQty, 0);
    const soldQty = rawLines.reduce((s, l) => s + l.soldQty, 0);
    const remainingQty = rawLines.reduce((s, l) => s + l.remainingQty, 0);
    const omzet = rawLines.reduce((s, l) => s + l.subtotal, 0);
    const selisih = pembayaran - omzet;

    riders.push({
      riderId: entry.riderId,
      riderName: entry.riderName,
      status,
      workDaysCount: entry.salesCount,
      soldQty,
      remainingQty,
      dispatchedQty,
      omzet,
      qris,
      cash,
      other,
      pembayaran,
      selisih,
      bonus: entry.totalBonus,
      notes: entry.notesList.length > 0 ? entry.notesList.join("; ") : null,
      lines: rawLines,
    });
  }

  // Sort riders
  riders.sort((a, b) => {
    if (riderOrder) {
      const idxA = riderOrder.indexOf(a.riderId);
      const idxB = riderOrder.indexOf(b.riderId);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
    }
    return a.riderName.localeCompare(b.riderName);
  });

  // 4. Overall Totals
  const totals = {
    soldQty: riders.reduce((s, r) => s + r.soldQty, 0),
    remainingQty: riders.reduce((s, r) => s + r.remainingQty, 0),
    dispatchedQty: riders.reduce((s, r) => s + r.dispatchedQty, 0),
    omzet: riders.reduce((s, r) => s + r.omzet, 0),
    qris: riders.reduce((s, r) => s + r.qris, 0),
    cash: riders.reduce((s, r) => s + r.cash, 0),
    other: riders.reduce((s, r) => s + r.other, 0),
    pembayaran: riders.reduce((s, r) => s + r.pembayaran, 0),
    selisih: riders.reduce((s, r) => s + r.selisih, 0),
    bonus: riders.reduce((s, r) => s + r.bonus, 0),
  };

  // 5. Product rekap per category
  const productCategoryMap = new Map<
    ProductCategory,
    Map<string, ProductStockRekapItem>
  >();

  for (const rider of riders) {
    for (const line of rider.lines) {
      let catMap = productCategoryMap.get(line.category);
      if (!catMap) {
        catMap = new Map();
        productCategoryMap.set(line.category, catMap);
      }
      const prodKey = line.productId || line.productName;
      let pItem = catMap.get(prodKey);
      if (!pItem) {
        pItem = {
          productId: line.productId,
          productName: line.productName,
          category: line.category,
          diambil: 0,
          terjual: 0,
          sisa: 0,
          omzet: 0,
        };
        catMap.set(prodKey, pItem);
      }
      pItem.diambil += line.dispatchedQty;
      pItem.terjual += line.soldQty;
      pItem.sisa += line.remainingQty;
      pItem.omzet += line.subtotal;
    }
  }

  // Order categories: beverage first, then bakery, then others
  const categoryOrder: ProductCategory[] = [
    "beverage",
    "bakery",
    "food",
    "add_on",
    "merchandise",
    "other",
  ];

  const rekapCategories: ProductStockRekapCategory[] = [];
  let totalMinumanCup = 0;
  let totalRotiPcs = 0;

  for (const cat of categoryOrder) {
    const catMap = productCategoryMap.get(cat);
    if (!catMap) continue;

    const items = Array.from(catMap.values()).filter(
      (item) => item.diambil > 0,
    );

    if (items.length === 0) continue;

    items.sort((a, b) => {
      if (productOrder) {
        const idxA = productOrder.indexOf(a.productId);
        const idxB = productOrder.indexOf(b.productId);
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
      }
      return a.productName.localeCompare(b.productName);
    });

    const totalDiambil = items.reduce((s, i) => s + i.diambil, 0);
    const totalTerjual = items.reduce((s, i) => s + i.terjual, 0);
    const totalSisa = items.reduce((s, i) => s + i.sisa, 0);
    const totalOmzet = items.reduce((s, i) => s + i.omzet, 0);

    let title = `Rekap stok ${cat}`;
    let unit = "pcs";
    if (cat === "beverage") {
      title = "Rekap stok minuman (cup)";
      unit = "cup";
      totalMinumanCup = totalTerjual;
    } else if (cat === "bakery") {
      title = "Rekap stok roti (pcs)";
      unit = "pcs";
      totalRotiPcs = totalTerjual;
    } else {
      title = `Rekap stok ${cat} (pcs)`;
      unit = "pcs";
    }

    rekapCategories.push({
      category: cat,
      title,
      unit,
      items,
      totalDiambil,
      totalTerjual,
      totalSisa,
      totalOmzet,
    });
  }

  // 6. Status and issue summary
  const draftCount = riders.filter((r) => r.status === "draft").length;
  const discrepancyCount = riders.filter((r) => r.selisih !== 0).length;

  let status: "Selesai" | "DRAFT / BERMASALAH" = "Selesai";
  let issueSummary: string | undefined;

  if (draftCount > 0 || discrepancyCount > 0) {
    status = "DRAFT / BERMASALAH";
    const parts: string[] = [];
    if (draftCount > 0) {
      parts.push(`${draftCount} rider draft`);
    }
    if (discrepancyCount > 0) {
      parts.push(`${discrepancyCount} rider berselisih`);
    }
    issueSummary = `(Terdapat ${parts.join(", ")})`;
  }

  // 7. Summary sentences
  const sentenceOmzet = `Total omzet ${formatReportRp(totals.omzet)} dari ${totalMinumanCup} cup minuman dan ${totalRotiPcs} pcs roti.`;

  const payOtherPart =
    hasOtherPayment && totals.other > 0
      ? `; lainnya ${formatReportRp(totals.other)}`
      : "";
  const sentencePembayaran = `Pembayaran ${formatReportRp(totals.pembayaran)}: QRIS ${formatReportRp(totals.qris)}; tunai ${formatReportRp(totals.cash)}${payOtherPart}. Selisih ${formatReportRp(totals.selisih)}.`;

  // 8. Discrepancy notes
  const discrepancyNotes: { riderName: string; note: string }[] = [];
  for (const rider of riders) {
    if (rider.selisih !== 0) {
      const noteText =
        rider.notes ||
        (rider.selisih < 0
          ? `kurang setor ${formatReportRp(Math.abs(rider.selisih))}`
          : `lebih setor ${formatReportRp(rider.selisih)}`);
      discrepancyNotes.push({
        riderName: rider.riderName,
        note: noteText,
      });
    }
  }

  // 9. Chunk riders for Detail Pages (4 riders per page in 2x2 grid)
  const riderPages: RiderReportSummaryRow[][] = [];
  for (let i = 0; i < riders.length; i += 4) {
    riderPages.push(riders.slice(i, i + 4));
  }

  return {
    dateFrom,
    dateTo,
    isMultiDay,
    formattedPeriod,
    workspaceName,
    status,
    draftCount,
    discrepancyCount,
    issueSummary,

    totalOmzet: totals.omzet,
    totalMinumanCup,
    totalRotiPcs,
    sentenceOmzet,

    totalPembayaran: totals.pembayaran,
    totalQris: totals.qris,
    totalCash: totals.cash,
    totalOther: totals.other,
    totalSelisih: totals.selisih,
    totalBonus: totals.bonus,
    hasOtherPayment,
    sentencePembayaran,

    riders,
    totals,
    discrepancyNotes,
    rekapCategories,
    riderPages,
  };
}
